// Canonical ST-2026 domain types.
// Single source of truth for the dashboard; mirrors docs/06-API-CONTRACT.md §1.5.
// If this ever disagrees with Worker A's shipped API, A wins — flag the drift in ORCHESTRATION.md.

export type UnitRole = 'HQ' | 'PLAYER' | 'CONTROL' | 'UMPIRE';

export type PlatoonPhase =
  | 'SECURITY'
  | 'MOVING_OUT'
  | 'RECCE'
  | 'HIDEOUT'
  | 'DRILLS'
  | 'BRIEF'
  | 'EXECUTING'
  | 'FALLBACK'
  | 'RECOVERED';

export type OperationType =
  | 'PATROLLING'
  | 'RECCE'
  | 'AMBUSH'
  | 'RAID'
  | 'HIDEOUT'
  | 'INFILTRATION'
  | 'CACHE_HUNT';

export type OperationStatus = 'PLANNED' | 'ACTIVE' | 'COMPLETED' | 'ABORTED';

export type GeofenceType =
  | 'FARMBASE'
  | 'TARGET'
  | 'HIDEOUT'
  | 'RV'
  | 'BOUNDARY'
  | 'CHECKPOINT';

export type SOSTriggerType = 'BUTTON' | 'SHAKE' | 'POWER_KEY' | 'VOLUME_KEYS' | 'AUTOMATIC';
export type SOSStatus = 'TRIGGERED' | 'ACKNOWLEDGED' | 'RESPONDING' | 'RESOLVED' | 'CANCELLED';

// The three coarse rotation lanes a farmbase's 3 platoons should occupy (dashboard spec §4).
export type RotationLane = 'SECURITY' | 'RECCE' | 'EXECUTION';

export interface Unit {
  id: string;
  code: string;
  name: string;
  role: UnitRole;
  parentFormation: string;
  aorColor: string;
  aorStyle: string;
  logoUrl: string | null;
  isActive: boolean;
}

export interface DeviceInfo {
  id: string;
  status: string;
  lastSeenAt: string | null;
  batteryLevel: number | null;
  signalStrength: number | null;
}

export interface Platoon {
  id: string;
  callsign: string;
  platoonNumber: number;
  unitId: string;
  farmbaseId: string | null;
  deviceId: string | null;
  currentPhase: PlatoonPhase;
  currentOperationId: string | null;
  commanderName: string | null;
  strength: number | null;
  sos: boolean;
  unit?: Pick<Unit, 'id' | 'code' | 'name'>;
  farmbase?: Pick<Farmbase, 'id' | 'name'>;
  device?: DeviceInfo;
}

export interface Farmbase {
  id: string;
  name: string;
  unitId: string;
  anchorGeofenceId: string | null;
  status: string;
  // Round-2 (docs/14 §2.1): command + manpower + editable geometry.
  commanderName?: string | null;
  manpowerOfficers?: number;
  manpowerJCOs?: number;
  manpowerORs?: number;
  // Geometry is NESTED on the wire (§15/§16): the DTO returns `geometry: {…}` (or null).
  // Prefer this when reading a saved farmbase back into a form.
  geometry?: Geometry | null;
  // Legacy flat mirrors (still consumed by some map/summary helpers).
  shape?: GeoShape | null;
  centerLat?: number | null;
  centerLon?: number | null;
  radiusMeters?: number | null;
  coords?: LatLon[] | null;
  // Representative point (§16): POINT=center, LINE=midpoint, BOX=centroid.
  repLat?: number | null;
  repLng?: number | null;
  // Present on GET /farmbases/:id (the rotation snapshot is the payload's whole point).
  platoons?: Platoon[];
  // phase -> platoonId snapshot, e.g. { SECURITY: "plt-1", RECCE: "plt-2", EXECUTING: "plt-3" }
  rotation?: Partial<Record<PlatoonPhase, string>>;
}

export interface OperationEvent {
  id: string;
  operationId: string | null;
  phase: PlatoonPhase;
  timestamp: string;
  lat?: number | null;
  lng?: number | null;
  note?: string | null;
}

export interface Operation {
  id: string;
  type: OperationType;
  platoonId: string;
  unitId: string;
  targetGeofenceId: string | null;
  hideoutGeofenceId: string | null;
  status: OperationStatus;
  night: number | null;
  startedAt: string | null;
  completedAt: string | null;
  platoon?: Pick<Platoon, 'id' | 'callsign'>;
  events?: OperationEvent[];
}

export interface Geofence {
  id: string;
  name: string;
  type: GeofenceType;
  description: string | null;
  centerLat: number | null;
  centerLon: number | null;
  radiusMeters: number | null;
  polygonCoords: [number, number][] | null;
  alertOnEntry: boolean;
  alertOnExit: boolean;
  maxDwellMinutes: number | null;
  isActive: boolean;
  unitId: string | null;
  metadata: Record<string, unknown> | null;
}

// ---------------------------------------------------------------------------
// Phase palette — the ONLY place phase colors/labels/lanes are defined.
// Every phase-colored surface (marker, badge, legend, rotation board, timeline)
// reads PHASE_META. Tailwind mirrors these under theme.extend.colors.phase.
// ---------------------------------------------------------------------------

export type PhaseTreatment = 'ring' | 'pulse' | 'solid' | 'glow' | 'dashed' | 'dim';

export const PHASE_META: Record<
  PlatoonPhase,
  { label: string; color: string; lane: RotationLane; treatment: PhaseTreatment }
> = {
  SECURITY: { label: 'Security', color: '#3399ff', lane: 'SECURITY', treatment: 'ring' },
  MOVING_OUT: { label: 'Moving Out', color: '#00d4aa', lane: 'RECCE', treatment: 'pulse' },
  RECCE: { label: 'Recce', color: '#00cc66', lane: 'RECCE', treatment: 'pulse' },
  HIDEOUT: { label: 'Hideout', color: '#ffaa00', lane: 'EXECUTION', treatment: 'solid' },
  DRILLS: { label: 'Drills', color: '#ffcc00', lane: 'EXECUTION', treatment: 'solid' },
  BRIEF: { label: 'Brief', color: '#c78bff', lane: 'EXECUTION', treatment: 'solid' },
  EXECUTING: { label: 'Executing', color: '#ff6b00', lane: 'EXECUTION', treatment: 'glow' },
  FALLBACK: { label: 'Fallback', color: '#ff2d55', lane: 'EXECUTION', treatment: 'dashed' },
  RECOVERED: { label: 'Recovered', color: '#6b7280', lane: 'EXECUTION', treatment: 'dim' },
};

export const PLATOON_PHASES = Object.keys(PHASE_META) as PlatoonPhase[];

// Rotation-board lane definitions (dashboard spec §4). RECOVERED drops out of the
// lanes into a "cycle complete" footer, so it is not mapped to a lane here.
export const ROTATION_LANES: { lane: RotationLane; label: string; phases: PlatoonPhase[] }[] = [
  { lane: 'SECURITY', label: 'Security', phases: ['SECURITY'] },
  { lane: 'RECCE', label: 'Recce', phases: ['MOVING_OUT', 'RECCE'] },
  {
    lane: 'EXECUTION',
    label: 'Hideout / Execution',
    phases: ['HIDEOUT', 'DRILLS', 'BRIEF', 'EXECUTING', 'FALLBACK'],
  },
];

export function laneForPhase(phase: PlatoonPhase): RotationLane | null {
  if (phase === 'RECOVERED') return null;
  const found = ROTATION_LANES.find((l) => l.phases.includes(phase));
  return found ? found.lane : null;
}

export function phaseColor(phase: PlatoonPhase): string {
  return PHASE_META[phase]?.color ?? '#6b7280';
}

// ===========================================================================
// Round 2 — Command Center (docs/14). Geometry, Targets, TargetAssignment,
// ExerciseEvent (war diary), and their presentation metadata.
// ===========================================================================

// Proximity threshold — a code constant, not env (locked decision, docs/14 §8).
export const TARGET_PROXIMITY_M = 200;

export type GeoShape = 'POINT' | 'LINE' | 'BOX';

export interface LatLon {
  lat: number;
  lon: number;
}

// Optional per-object draw styling (§15/§16). Persisted and echoed nested under
// `geometry.style`. All fields optional — an absent style falls back to the
// status/type-derived defaults each layer already uses.
export interface GeometryStyle {
  strokeColor?: string;
  fillColor?: string;
  dashed?: boolean;
  weight?: number; // stroke thickness, 1–8
  opacity?: number; // stroke opacity, 0–1
  fillOpacity?: number; // fill opacity, 0–1
}

// The geometry wire shape B/C consume (docs/14 §1). POINT = marker + circle;
// LINE = 2 coords (polyline); BOX = 4 coords (closed polygon).
export interface Geometry {
  shape: GeoShape;
  centerLat?: number | null;
  centerLon?: number | null;
  radiusMeters?: number | null;
  coords?: LatLon[] | null;
  style?: GeometryStyle;
}

export type TargetType = 'STATIONARY' | 'MOVING' | 'SUPPLY';

export type TargetStatus =
  | 'INACTIVE'
  | 'ACTIVE'
  | 'ASSIGNED'
  | 'EXECUTING'
  | 'ELIMINATED'
  | 'FAILED'
  | 'EXPIRED';

export interface TargetAssignment {
  id: string;
  // Not present on the nested read (§17 inlines assignments under the target).
  targetId?: string;
  platoonId: string;
  // §17 inlines the platoon callsign on the assignment — no deep platoon object.
  callsign?: string | null;
  assignedById?: string | null;
  assignedAt?: string | null;
  notifiedAt?: string | null;
  executionStartedAt?: string | null;
  executionStoppedAt?: string | null;
  operationId?: string | null;
  platoon?: Pick<Platoon, 'id' | 'callsign' | 'unitId' | 'currentPhase'>;
}

export interface Target extends Geometry {
  id: string;
  name: string;
  type: TargetType;
  // Nested geometry as returned by the DTO (§17); mirrors the flat fields inherited
  // from Geometry. Prefer this when reading a saved target back into a form.
  geometry?: Geometry | null;
  enemyManpower?: number | null;
  openingTime?: string | null;
  closingTime?: string | null;
  status: TargetStatus;
  unitId?: string | null; // null = global target (HQ-visible only), §17
  // Server-derived (§14/§15): required op for the type + representative point.
  requiredOperation?: 'RAID' | 'AMBUSH' | 'CACHE_HUNT';
  repLat?: number | null;
  repLng?: number | null;
  createdById?: string | null;
  assignments?: TargetAssignment[];
  createdAt?: string;
  updatedAt?: string;
}

// The war-diary event stream (docs/14 §2.4). Kept open (| string) so A can add
// event types without breaking the dashboard.
export type ExerciseEventType =
  | 'PHASE_TAG'
  | 'GEOFENCE_CROSS'
  | 'SOS'
  | 'SOS_RESOLVED'
  | 'TARGET_CREATED'
  | 'TARGET_STATUS'
  | 'TARGET_ASSIGNED'
  | 'PROXIMITY_200M'
  | 'EXECUTION_START'
  | 'EXECUTION_STOP'
  | 'ADJUDICATION'
  | 'COMMS_LOST'
  | 'COMMS_RESTORED'
  | (string & {});

// Mirrors a GET /feed item (§19.1) — also the payload of the feed:event socket (§20).
export interface ExerciseEvent {
  id: string;
  type: ExerciseEventType;
  timestamp: string;
  unitId?: string | null;
  platoonId?: string | null;
  targetId?: string | null;
  severity?: string | null;
  summary: string;
  metadata?: string | Record<string, unknown> | null;
  // Nested descriptors the feed inlines for display / CSV (unitCode via unitId lookup).
  platoon?: { id: string; callsign: string } | null;
  target?: { id: string; name: string } | null;
}

// Per-platoon comms status (docs/14 §6 "who's dark"). Signal is omitted unless real.
export interface CommsStatus {
  platoonId: string;
  callsign?: string;
  unitId?: string | null;
  lastSeenAt?: string | null;
  batteryLevel?: number | null;
  signalStrength?: number | null;
  dark?: boolean;
}

// ---- Presentation metadata (single source of truth) ----

// Target type → required operation + how it's drawn (docs/14 §2.2).
export const TARGET_TYPE_META: Record<
  TargetType,
  { label: string; operation: Extract<OperationType, 'RAID' | 'AMBUSH' | 'CACHE_HUNT'>; shape: GeoShape; color: string; icon: string }
> = {
  STATIONARY: { label: 'Stationary', operation: 'RAID', shape: 'POINT', color: '#ff4444', icon: '🎯' },
  MOVING: { label: 'Moving', operation: 'AMBUSH', shape: 'LINE', color: '#ff8800', icon: '🚚' },
  SUPPLY: { label: 'Supply', operation: 'CACHE_HUNT', shape: 'POINT', color: '#c78bff', icon: '📦' },
};

export const TARGET_TYPES = Object.keys(TARGET_TYPE_META) as TargetType[];

export const TARGET_STATUS_META: Record<
  TargetStatus,
  { label: string; color: string; dim?: boolean; hiddenByDefault?: boolean }
> = {
  INACTIVE: { label: 'Not active yet', color: '#6b7280', dim: true },
  ACTIVE: { label: 'Active', color: '#00cc66' },
  ASSIGNED: { label: 'Assigned', color: '#3399ff' },
  EXECUTING: { label: 'Executing', color: '#ff6b00' },
  ELIMINATED: { label: 'Eliminated', color: '#22c55e' },
  FAILED: { label: 'Failed', color: '#ff2d55' },
  EXPIRED: { label: 'Expired', color: '#4b5563', dim: true, hiddenByDefault: true },
};

// Live battle-view treatment per operation type (docs/14 §3.7 — visually distinct).
export const OP_BATTLE_META: Record<
  'RAID' | 'AMBUSH' | 'CACHE_HUNT',
  { label: string; color: string; emoji: string; pulseMs: number }
> = {
  RAID: { label: 'RAID', color: '#ff2d55', emoji: '💥', pulseMs: 900 },
  AMBUSH: { label: 'AMBUSH', color: '#ff8800', emoji: '⚡', pulseMs: 1200 },
  CACHE_HUNT: { label: 'CACHE HUNT', color: '#c78bff', emoji: '📦', pulseMs: 1600 },
};

export function operationForTarget(type: TargetType): 'RAID' | 'AMBUSH' | 'CACHE_HUNT' {
  return TARGET_TYPE_META[type].operation;
}
